import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { ClientForm } from '@/components/clients/ClientForm'
import type { Client } from '@/types'

// Mirrors dialog.tsx: Select/Popover portals can false-dismiss a modal Dialog
// when pointer-events:none lets clicks hit the overlay (radix-ui/primitives#2961).
const pointerDownWithPopperOpen = new WeakSet<Event>()
if (typeof document !== 'undefined') {
  document.addEventListener(
    'pointerdown',
    event => {
      if (document.querySelector('[data-radix-popper-content-wrapper]')) {
        pointerDownWithPopperOpen.add(event)
      }
    },
    true
  )
}

export type ClientFormTarget = { mode: 'create' } | { mode: 'edit'; id: string }

interface ClientFormDialogProps {
  target: ClientFormTarget | null
  onClose: () => void
  onSuccess: (client: Client) => void
}

/**
 * Right-side create/edit client panel (Figma slider). Built on Radix Dialog
 * like JobDetailDrawer so the clients list stays mounted underneath.
 */
export function ClientFormDialog({
  target,
  onClose,
  onSuccess,
}: ClientFormDialogProps) {
  const { t } = useTranslation('clients')
  const open = target !== null
  const mode = target?.mode ?? 'create'
  const clientId = target?.mode === 'edit' ? target.id : undefined
  const title = mode === 'edit' ? t('form.editTitle') : t('form.createTitle')

  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={next => {
        if (!next) onClose()
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className='fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0' />
        <DialogPrimitive.Content
          className='tf-right-drawer-fixed-short duration-300 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right'
          aria-describedby={undefined}
          onInteractOutside={event => {
            const original = event.detail.originalEvent
            const eventTarget = original.target as Element | null
            if (
              pointerDownWithPopperOpen.has(original) ||
              eventTarget?.closest('[data-radix-popper-content-wrapper]')
            ) {
              event.preventDefault()
            }
          }}
        >
          <header className='tf-right-drawer-header tf-right-drawer-header-accent'>
            <DialogPrimitive.Title className='min-w-0 flex-1 font-heading text-base font-semibold leading-[1.2] text-[#171a20]'>
              {title}
            </DialogPrimitive.Title>
            <DialogPrimitive.Close asChild>
              <Button
                type='button'
                variant='ghost'
                size='icon'
                className='size-9 shrink-0 rounded-xl bg-[#f4f5f8] hover:bg-[#f4f5f8]'
                aria-label={t('form.cancel')}
              >
                <X className='size-4' />
              </Button>
            </DialogPrimitive.Close>
          </header>

          {target && (
            <ClientForm
              key={`${mode}-${clientId ?? 'new'}`}
              mode={mode}
              clientId={clientId}
              onCancel={onClose}
              onSuccess={onSuccess}
            />
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
